import { apiService } from "@/api";
import { Session } from "@/models";
import { NewTaskService } from "@/services";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ConversationPanelLeft from "../components/content/ConversationPanelLeft";
import ConversationView from "../components/content/ConversationView";
import InlineToaster from "../components/toast/InlineToaster";
import Content from "../coral/components/Content/Content";
import ContentToolbar from "../coral/components/Content/ContentToolbar";
import CoralShellColumn from "../coral/components/Layout/CoralShellColumn";
import CoralShellRow from "../coral/components/Layout/CoralShellRow";
import "../styles/PlanPage.css";

/**
 * Page component for continuous conversation
 * Accessible via the route /conversation/{session_id}
 */
const ConversationPage: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const [session, setSession] = useState<Session | null>(null);

    const loadSession = useCallback(async () => {
        if (!sessionId) return;
        try {
            const sessions = await apiService.getConversationSessions(true);
            const currentSession = sessions.find(s => s.id === sessionId);
            setSession(currentSession || null);
        } catch (error) {
            console.error("Failed to load session:", error);
        }
    }, [sessionId]);

    useEffect(() => {
        loadSession();
    }, [loadSession]);

    // Listen for session refresh events (triggered when first message response arrives)
    useEffect(() => {
        const cleanup = NewTaskService.addSessionsRefreshListener(() => {
            loadSession();
        });
        return cleanup;
    }, [loadSession]);

    if (!sessionId) {
        return (
            <div className="content-not-found">
                <p>Invalid conversation session</p>
            </div>
        );
    }

    const pageTitle = session?.message_to_user || "New conversation";

    return (
        <CoralShellColumn>
            <CoralShellRow>
                <ConversationPanelLeft />

                <Content>
                    <ContentToolbar
                        panelTitle={pageTitle}
                    />
                    <ConversationView sessionId={sessionId} />
                    <InlineToaster />
                </Content>
            </CoralShellRow>
        </CoralShellColumn>
    );
};

export default ConversationPage;
